import { pathScreens } from "../../../shared/tour";
import type { Hotspot, HotspotAction, Step } from "../../../shared/types";
import { IconArrowRight, IconExternal, IconJump, IconMessage } from "../../icons";
import { clamp, MIN_HOTSPOT_PCT } from "../hotspotCanvas";
import { useEditor } from "../DemoEditor";

const ACTIONS: { value: HotspotAction; short: string; icon: React.ReactNode }[] = [
  { value: "advance_next", short: "Next screen", icon: <IconArrowRight /> },
  { value: "goto_step", short: "Jump to screen", icon: <IconJump /> },
  { value: "show_tooltip", short: "Tooltip", icon: <IconMessage /> },
  { value: "open_link", short: "Open a link", icon: <IconExternal /> },
];

const isTinyHotspot = (hs: Hotspot) => hs.w < MIN_HOTSPOT_PCT || hs.h < MIN_HOTSPOT_PCT;

export function HotspotProps({ h, n, steps, currentStepId, onChange, onDelete }: {
  h: Hotspot; n: number; steps: Step[]; currentStepId: string; onChange: (p: Partial<Hotspot>) => void; onDelete: () => void;
}) {
  const { bundle } = useEditor();
  // Every path's screens, in that path's order (a screen on several paths is listed under each).
  const all = { steps, outcomes: bundle.outcomes };
  const groups = [
    { label: "Default path", list: pathScreens(all, null) },
    ...bundle.outcomes.map((o) => ({ label: o.name, list: pathScreens(all, o.id) })),
  ];
  return (
    <div className="panel stack-s">
      <h3>Hotspot {n}</h3>
      <div className="field"><span id="hs-action-label">When clicked</span>
        <div className="action-cards" role="radiogroup" aria-labelledby="hs-action-label">
          {ACTIONS.map((a) => (
            <button key={a.value} type="button" role="radio" aria-checked={h.action === a.value} className={`action-card ${h.action === a.value ? "on" : ""}`} onClick={() => onChange({ action: a.value })}>
              {a.icon}<span>{a.short}</span>
            </button>
          ))}
        </div>
      </div>
      {h.action === "goto_step" && (
        <label className="field"><span>Target screen <span className="hint">If it isn't in the viewer's path, this acts as Next</span></span>
          <select value={h.targetStepId ?? ""} onChange={(e) => onChange({ targetStepId: e.target.value || null })}>
            <option value="">Choose a screen</option>
            {groups.map((g) => g.list.length > 0 && (
              <optgroup key={g.label} label={g.label}>
                {g.list.map((s, i) => (
                  <option key={s.id} value={s.id} disabled={s.id === currentStepId}>
                    Screen {i + 1}{s.caption ? `: ${s.caption.slice(0, 40)}` : ""}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
      )}
      {h.action !== "open_link" && (
        <label className="field">
          <span>{h.action === "show_tooltip" ? <>Tooltip text <span className="hint">Try {"{{company|your team}}"}</span></> : "Label for screen readers (optional)"}</span>
          <textarea value={h.tooltipText} maxLength={500} onChange={(e) => onChange({ tooltipText: e.target.value })} />
        </label>
      )}
      {h.action === "open_link" && (
        <label className="field"><span>Link</span>
          <input type="url" placeholder="https://" value={h.linkUrl} onChange={(e) => onChange({ linkUrl: e.target.value.trim() })} />
        </label>
      )}
      <label className="check"><input type="checkbox" checked={h.pulse} onChange={(e) => onChange({ pulse: e.target.checked })} />Pulse to draw attention</label>
      <div className="grid-2 small">
        {(["x", "y", "w", "h"] as const).map((k) => (
          <label key={k} className="field"><span>{{ x: "Left %", y: "Top %", w: "Width %", h: "Height %" }[k]}</span>
            <input type="number" step={0.5} min={0} max={100} value={h[k]} onChange={(e) => onChange({ [k]: clamp(Number(e.target.value) || 0, k === "w" || k === "h" ? MIN_HOTSPOT_PCT : 0, 100) })} />
          </label>
        ))}
      </div>
      {isTinyHotspot(h) && (
        <p className="small hint">This is smaller than most fingers or cursors can reliably hit — consider enlarging it.</p>
      )}
      <button className="btn btn-danger btn-sm" onClick={onDelete}>Delete hotspot</button>
    </div>
  );
}
